import { useCallback, useEffect, useState } from 'react'
import { isNullOrUndefined } from '@/util/typeHelper'
import {
  calculateRemainingSeconds,
  formatRemainingSeconds,
  RemainingPrecision
} from '../utils'
import * as Arcade from '@/features/arcade/types'

type RemainingBefore = { isBefore: boolean; remaining: number }

const INTERVAL_LONG_MS = 10000
const INTERVAL_NORMAL_MS = 1000
const INTERVAL_SHORT_MS = 500

const calculateInterval = (
  remainingSeconds: Arcade.Seconds,
  precision: RemainingPrecision
): number | null => {
  if (remainingSeconds <= 0) return null
  if (precision === 'high') return INTERVAL_SHORT_MS
  if (precision === 'medium') return INTERVAL_NORMAL_MS
  return INTERVAL_LONG_MS
}

const COUNTDOWN_DONE = {
  isBefore: false,
  isAfter: true,
  remaining: 0
}

const calculateRemainingBefore = (
  seconds: Arcade.Seconds,
  timestamp?: Arcade.Timestamp
): RemainingBefore => {
  if (isNullOrUndefined(timestamp)) return COUNTDOWN_DONE

  const remainingSeconds = calculateRemainingSeconds(timestamp)
  const remaining = Math.max(seconds, remainingSeconds)
  const isBefore = remaining > 0

  return { isBefore, remaining }
}

type CountdownInput = {
  timestamp?: Arcade.Timestamp
  doneMessage?: string
  precision?: RemainingPrecision
}

export const useCountdown = ({
  timestamp,
  doneMessage,
  precision = 'high'
}: CountdownInput = {}): Arcade.SeasonCountdown => {
  const [seconds, setSeconds] = useState<Arcade.Seconds>(
    calculateRemainingSeconds(timestamp)
  )

  const remainingBefore = calculateRemainingBefore(seconds, timestamp)
  const interval = calculateInterval(remainingBefore.remaining, precision)

  const tick = useCallback(() => {
    if (remainingBefore.isBefore) {
      setSeconds(calculateRemainingSeconds(timestamp))
    }
  }, [remainingBefore])

  useEffect(() => {
    if (interval === null) return
    const id = setInterval(tick, interval)
    return () => clearInterval(id)
  }, [interval, tick])

  const { isBefore, remaining } = remainingBefore

  return {
    isBefore,
    isAfter: !isBefore,
    remaining,
    formatted: formatRemainingSeconds(remaining, precision, doneMessage)
  }
}

type SeasonStartsInput = {
  season: Pick<Arcade.Season, 'startAt' | 'status'> | null | undefined
  precision?: RemainingPrecision
}

export type SeasonStartsResult = {
  seasonStarts: Arcade.SeasonCountdown
}

export const useArcadeSeasonStarts = (
  input: SeasonStartsInput
): SeasonStartsResult => {
  const { season, precision } = input

  const timestamp = (season?.status === 'next' && season?.startAt) || null
  const countdown = useCountdown({
    timestamp,
    doneMessage: 'Started',
    precision
  })
  return { seasonStarts: countdown }
}

type SeasonEndsInput = {
  season: Pick<Arcade.Season, 'endAt' | 'status'> | null | undefined
  precision?: RemainingPrecision
}

export type SeasonEndsResult = {
  seasonEnds: Arcade.SeasonCountdown
}

export const useArcadeSeasonEnds = (
  input: SeasonEndsInput
): SeasonEndsResult => {
  const { season, precision } = input
  const timestamp = (season?.status === 'current' && season?.endAt) || null
  const countdown = useCountdown({
    timestamp,
    doneMessage: 'Ended',
    precision
  })
  return { seasonEnds: countdown }
}
